import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useState, useRef, useCallback } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import type { Enums, Tables, TablesUpdate } from "@/integrations/supabase/types";
// UPPDRAG-15: per-court sponsor management. Collapsible sub-section
// inside each court Card.
import CourtSponsorsManager from "@/components/admin/CourtSponsorsManager";

type CourtWithHall = Tables<"courts"> & {
  halls?: { name: string; opening_time: string; closing_time: string } | null;
};

const STATUS_KEYS: Record<Enums<"court_status">, string> = { active: "courtsPage.statusActive", closed: "courtsPage.statusClosed", maintenance: "courtsPage.statusMaintenance" };
const TYPE_KEYS: Record<string, string> = { single: "courtsPage.typeSingle", double: "courtsPage.typeDouble", panorama: "courtsPage.typePanorama", standard: "courtsPage.typeStandard", indoor: "courtsPage.typeIndoor", outdoor: "courtsPage.typeOutdoor" };

export default function AdminCourts() {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  const { courts, hallId } = useOrgHall();
  const [editingName, setEditingName] = useState<Record<string, string>>({});
  const [editingGroup, setEditingGroup] = useState<Record<string, string>>({});
  const groupTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const debouncedGroupUpdate = useCallback((courtId: string, value: string | null) => {
    if (groupTimers.current[courtId]) clearTimeout(groupTimers.current[courtId]);
    groupTimers.current[courtId] = setTimeout(() => {
      updateCourt.mutate({ id: courtId, updates: { court_group: value } });
    }, 1500);
  }, []);

  const updateCourt = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TablesUpdate<"courts"> }) => {
      const { error } = await supabase.from("courts").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org-courts"] }); toast.success(t("courtsPage.courtUpdated")); },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("courtsPage.couldNotUpdate")); },
  });

  const addCourt = useMutation({
    mutationFn: async () => {
      if (!hallId) throw new Error("No hall");
      const { error } = await supabase.from("courts").insert({
        hall_id: hallId,
        name: `${t("scheduleGrid.court")} ${courts.length + 1}`,
        status: "active" as const,
        court_type: "double" as const,
        sort_order: courts.length,
      });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org-courts"] }); toast.success(t("courtsPage.courtAdded")); },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("courtsPage.couldNotAdd")); },
  });

  const deleteCourt = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("courts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org-courts"] }); toast.success(t("courtsPage.courtRemoved")); },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("courtsPage.couldNotRemove")); },
  });

  const swapOrder = (indexA: number, indexB: number) => {
    const courtA = courts[indexA] as CourtWithHall;
    const courtB = courts[indexB] as CourtWithHall;
    if (!courtA || !courtB) return;
    updateCourt.mutate({ id: courtA.id, updates: { sort_order: courtB.sort_order } });
    updateCourt.mutate({ id: courtB.id, updates: { sort_order: courtA.sort_order } });
  };

  return (
    <div className="space-y-3">
      <Button onClick={() => addCourt.mutate()} disabled={!hallId || addCourt.isPending} className="w-full">
        <Plus className="h-4 w-4 mr-1" /> {t("courtsPage.addCourt")}
      </Button>

      {(courts as CourtWithHall[]).map((court, index) => (
        <Card key={court.id}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Input
                className="font-medium h-8 max-w-[180px]"
                value={editingName[court.id] ?? court.name}
                onChange={(e) => setEditingName({ ...editingName, [court.id]: e.target.value })}
                onBlur={() => {
                  const newName = editingName[court.id];
                  if (newName && newName !== court.name) updateCourt.mutate({ id: court.id, updates: { name: newName } });
                }}
              />
              <div className="flex items-center gap-1">
                {/* V2 Issue 4.3: Status visas nu endast via Select nedan. Den
                 *  röd/grön/gul Badge som fanns här duplicerade statusen och
                 *  båda labels ("Aktiv") blev synliga sida-vid-sida. */}
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={index === 0} onClick={() => swapOrder(index, index - 1)}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={index === courts.length - 1} onClick={() => swapOrder(index, index + 1)}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("courtsPage.confirmDelete", { name: court.name })}</AlertDialogTitle>
                      <AlertDialogDescription>{t("courtsPage.deleteDescription")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteCourt.mutate(court.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        {t("common.delete")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={court.status} onValueChange={(v) => updateCourt.mutate({ id: court.id, updates: { status: v } })}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_KEYS).map(([k, key]) => <SelectItem key={k} value={k}>{t(key)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={court.court_type} onValueChange={(v) => updateCourt.mutate({ id: court.id, updates: { court_type: v } })}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_KEYS).map(([k, key]) => <SelectItem key={k} value={k}>{t(key)}</SelectItem>)}
                </SelectContent>
              </Select>
              <NumberInput
                min={1}
                max={20}
                className="h-8 text-xs w-16"
                title={t("courtsPage.maxPlayers")}
                value={court.max_players ?? 4}
                onValueChange={(v) => {
                  if (v >= 1 && v <= 20) updateCourt.mutate({ id: court.id, updates: { max_players: v } });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">{t("courtsPage.surface")}</span>
              <Input
                className="h-8 text-xs max-w-[140px]"
                placeholder={t("courtsPage.surfacePlaceholder")}
                value={court.surface_type ?? ""}
                onChange={(e) => updateCourt.mutate({ id: court.id, updates: { surface_type: e.target.value || null } })}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">{t("courtsPage.group")}</span>
              <Input
                className="h-8 text-xs max-w-[100px]"
                placeholder={t("courtsPage.groupPlaceholder")}
                value={editingGroup[court.id] ?? court.court_group ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingGroup((prev) => ({ ...prev, [court.id]: val }));
                  debouncedGroupUpdate(court.id, val || null);
                }}
              />
            </div>

            {/* UPPDRAG-15: per-court sponsors. Collapsible to keep the
                Banor list compact when sponsors aren't being edited.
                Sponsors render in BookingGrid's court header for any
                authenticated user in the org. */}
            <CourtSponsorsManager courtId={court.id} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
